import {List, Map as IMap, OrderedMap} from 'immutable';
import {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {Component, Plugins} from '@packages/systems/wfdl';
import {PluginName, getPluginName} from '@packages/systems/wfdl-Qualified';
import {generateVariantDataMap} from '@packages/systems/component-style-variants';
import {
  accumulateStyleBlocks,
  buildStyleBlock,
  createDefaultTagServerStyleBlock,
  createDefaultTagStyleBlockId,
  createStyleBlock,
  exportStyleBlock,
  sortByDepth,
  updateCssPropertyMap,
} from '@packages/shared/LegacyStyleSystem/utils/StyleBlockUtils';
import {
  getStyleBlockIdsFromComponentRender,
  type StyleBlockMapType,
} from '@packages/utilities/platform';
import {Expression} from '@packages/systems/wfdl-ast';
import {RawFileType} from '@packages/systems/core/records/FileRecord';
import {generateGuid} from '@packages/utilities/guid';
import {
  StyleBlock,
  StyleBlockVariant,
} from '@packages/systems/style-legacy/records';
import {getAssetIdsFromStyleBlock} from './assets';
import {emptyArray, emptyObject} from '@packages/utilities/fp/utils';
import {RawStyleBlockServerType} from '@packages/systems/core/utils/StyleTypes';
import {keepOnlyModeApplications} from '@packages/systems/core/utils/StyleVariableModesCssUtils';
import {DEFAULT_TAG_STYLES} from '@packages/shared/LegacyStyleSystem/constants/DefaultTagStyles';
import {RawStyleBlockType} from '@packages/systems/style-legacy/values';
import {parseVariantKey} from '@packages/systems/style-legacy/variants';
import {
  type InstalledLibraryTagStylesSettings,
  type TagStyleFields,
  isHiddenLibraryTagStyle,
  isLibraryTagStyle,
} from '../tagStyles';
import {weakMemo} from '@packages/utilities/memo';

/**
 * The library-local id of the block that carries a library's auto modes.
 */
export const DEFAULT_BODY_BLOCK_ID = createDefaultTagStyleBlockId('body');

/**
 * Whether `fields` is the block that carries a library's auto modes — the
 * `default-body` convention. `removeHiddenTagStyling` is the only caller;
 * kept separate so the second carve-out, `isComponentVariantEntry`, reads as
 * its own check rather than folded into this one.
 */
const isAutoModeCarrier = (fields: TagStyleFields): boolean =>
  fields.origin?.[1] === DEFAULT_BODY_BLOCK_ID;

/**
 * Whether `variantKey` scopes its entry to a component variant rather than to a
 * breakpoint or state. Only the component style variants system writes a
 * `VariantStateKey`, and a library-owned tag block carries the entries the
 * publisher's own components authored — so the key settles it with no component
 * map to consult.
 */
const isComponentVariantEntry = (variantKey: string): boolean =>
  // The base styleLess is not a variant key, and `parseVariantKey` warns in
  // development on anything it cannot read as a breakpoint.
  variantKey !== BASE_STYLE_LESS_KEY &&
  parseVariantKey(variantKey).variantState != null;

/** Filters out styles that have the `origin` field on them - i.e. styles for a Library Component.
 *
 * The `origin` field is only set when the style is directly related to a library.
 *
 * As a side note, once the component is converted to a Site Component, the origin field is removed.
 *  */
export function filterLibraryStyles(styles: Array<RawStyleBlockServerType>) {
  // Filter out styles that have the `origin` field set
  return styles.filter((style) => {
    return !style.data.origin;
  });
}

/**
 * Orders style blocks for CSS generation. At equal specificity the last rule
 * emitted wins, so this order *is* the cascade: default tag stubs, then
 * unranked library blocks, then each ranked library's tag styles by the
 * priority the site gave it, then the site — last, so it can always override a
 * library.
 *
 * A `fake` block is the default tag stub the Designer holds in place of a site
 * style the user hasn't created yet. It carries no `origin`, but it is not a
 * site override — it is the browser default everything else beats, so it leads.
 * Only the Style panel's ordering includes these; CSS generation drops them.
 */
function _createStyleBlocksForCascadeSorter<T>({
  installedLibrariesTagStylesSettings,
  readTagStyleFields,
}: {
  installedLibrariesTagStylesSettings: InstalledLibraryTagStylesSettings;
  readTagStyleFields: (block: T) => TagStyleFields;
}): (blocks: readonly T[]) => T[] {
  // Every governed library is ranked, applying or not: `order` is the position
  // the site gave it, and switching the tag styles off is not meant to move it.
  const rankByPluginId = new Map<string, number>();
  let rankCount = 0;
  for (const [pluginId, {order}] of Object.entries(
    installedLibrariesTagStylesSettings
  )) {
    rankByPluginId.set(pluginId, order);
    rankCount = Math.max(rankCount, order + 1);
  }

  return (blocks) => {
    const defaultTagBlocks: T[] = [];
    const unrankedLibraryBlocks: T[] = [];
    const rankedTagStyles: T[][] = Array.from(
      {length: rankCount},
      (): T[] => []
    );
    const siteBlocks: T[] = [];

    for (const block of blocks) {
      const fields = readTagStyleFields(block);
      // Rank 0 is a real rank, so this tests against `undefined` rather than
      // falsiness. Every rank fed `rankCount`, so a ranked block always finds
      // its bucket.
      const rank = isLibraryTagStyle(fields)
        ? rankByPluginId.get(getPluginName(fields.origin))
        : undefined;
      const bucket = rank === undefined ? undefined : rankedTagStyles[rank];

      if (bucket != null) {
        bucket.push(block);
      } else if (fields.fake) {
        defaultTagBlocks.push(block);
      } else if (fields.origin == null) {
        siteBlocks.push(block);
      } else {
        unrankedLibraryBlocks.push(block);
      }
    }

    return defaultTagBlocks.concat(
      unrankedLibraryBlocks,
      ...rankedTagStyles,
      siteBlocks
    );
  };
}

/**
 * The Designer's entry point, called on every canvas resync. A `StyleBlock`
 * carries the four fields flat, so it is its own projection. Memoized on the
 * settings, which the engine keeps reference-stable; the blocks layer only
 * guards against a repeat call.
 */
export const sortStyleBlocksForCascade = weakMemo(
  (installed: InstalledLibraryTagStylesSettings) => {
    const sort = _createStyleBlocksForCascadeSorter<StyleBlock>({
      installedLibrariesTagStylesSettings: installed,
      readTagStyleFields: (block) => block,
    });
    return weakMemo((blocks: StyleBlock[]): StyleBlock[] => sort(blocks));
  }
);

/**
 * The key the base `styleLess` takes in a by-variant projection. Not a
 * breakpoint or state, so it cannot collide with a real variant key.
 */
const BASE_STYLE_LESS_KEY = '__base__';

/**
 * What survives on a hidden library tag style, or `null` when nothing does.
 * The one place this rule lives; each consumer reaches it through its own
 * read/write projection, so the canvas and the compiler cannot disagree.
 *
 * Two carve-outs, decided per entry: the auto-mode carrier's variable mode
 * applications, and any entry scoped to a component variant. Everything else
 * is tag styling through and through and drops entirely.
 */
const removeHiddenTagStyling = (
  fields: TagStyleFields,
  styleLessByVariant: Record<string, string>
): Record<string, string> | null => {
  const keepsModeApplications = isAutoModeCarrier(fields);
  const reduced: Record<string, string> = {};
  let survives = false;

  for (const [variantKey, styleLess] of Object.entries(styleLessByVariant)) {
    let kept = '';
    if (isComponentVariantEntry(variantKey)) {
      // A component variant is the component's own override, not the library's
      // global tag styling, so switching the tag styles off leaves it applying.
      kept = styleLess;
    } else if (keepsModeApplications) {
      kept = keepOnlyModeApplications(styleLess);
    }

    reduced[variantKey] = kept;
    if (kept) {
      survives = true;
    }
  }

  return survives ? reduced : null;
};

const readStyleBlockStyleLess = (block: StyleBlock): Record<string, string> => {
  const styleLessByVariant: Record<string, string> = {
    [BASE_STYLE_LESS_KEY]: block.styleLess ?? '',
  };
  block.variants.forEach((variant, variantKey) => {
    styleLessByVariant[variantKey] = variant.get('styleLess') ?? '';
  });
  return styleLessByVariant;
};

/**
 * Reduces a hidden library tag style `StyleBlock` to whatever
 * `removeHiddenTagStyling` says survives, or `undefined` when nothing does.
 * The Designer's entry point; callers test `isHiddenLibraryTagStyle` first.
 */
export const removeHiddenStyleBlockTagStyling = (
  block: StyleBlock
): StyleBlock | undefined => {
  const reduced = removeHiddenTagStyling(block, readStyleBlockStyleLess(block));
  if (reduced == null) {
    return undefined;
  }

  return block
    .set('styleLess', reduced[BASE_STYLE_LESS_KEY] ?? '')
    .update('variants', (variants) =>
      variants
        .map((variant, variantKey) =>
          variant.set('styleLess', reduced[variantKey] ?? '')
        )
        // A variant left with nothing would otherwise emit an empty rule.
        .filter((variant) => Boolean(variant.get('styleLess')))
    );
};

/**
 * Projects a server style block into the fields the tag style predicates read.
 * The server splits them across the block and its `data`, where the Designer
 * holds all four flat on an Immutable record — the reason the ordering takes a
 * projection rather than a block.
 */
export const readRawTagStyleFields = (
  style: RawStyleBlockServerType
): TagStyleFields => ({
  type: style.data.type,
  fake: style.fake,
  comb: style.data.comb,
  origin: style.data.origin,
});

/**
 * The raw-block counterpart of the `StyleBlock` projection. The server keeps
 * a block's content under `data` where the Designer holds it flat, so only the
 * reading and writing differ — `removeHiddenTagStyling` decides what
 * survives for both.
 */
const readRawStyleBlockStyleLess = (
  style: RawStyleBlockServerType
): Record<string, string> => {
  const styleLessByVariant: Record<string, string> = {
    [BASE_STYLE_LESS_KEY]: style.data.styleLess ?? '',
  };
  for (const [variantKey, variant] of Object.entries(
    style.data.variants ?? {}
  )) {
    styleLessByVariant[variantKey] = variant?.styleLess ?? '';
  }
  return styleLessByVariant;
};

/**
 * The raw-block twin of {@link removeHiddenStyleBlockTagStyling}, for the
 * publish path — `compileStyles` gets raw blocks and never builds a
 * `StyleBlockState`.
 */
export const removeHiddenRawStyleBlockTagStyling = (
  style: RawStyleBlockServerType
): RawStyleBlockServerType | undefined => {
  const reduced = removeHiddenTagStyling(
    readRawTagStyleFields(style),
    readRawStyleBlockStyleLess(style)
  );
  if (reduced == null) {
    return undefined;
  }

  const variants: NonNullable<RawStyleBlockServerType['data']['variants']> = {};
  for (const [variantKey, variant] of Object.entries(
    style.data.variants ?? {}
  )) {
    const variantStyleLess = reduced[variantKey] ?? '';
    // A variant left with nothing would otherwise emit an empty rule.
    if (!variantStyleLess) {
      continue;
    }
    // Spread the variant so `sel` survives — the compiler skips a variant
    // without one.
    variants[variantKey] = {...variant, styleLess: variantStyleLess};
  }

  return {
    ...style,
    data: {
      ...style.data,
      styleLess: reduced[BASE_STYLE_LESS_KEY] ?? '',
      variants,
    },
  };
};

/** The style compiler's entry point. Not memoized: one run per publish. */
export const sortRawStyleBlocksForCascade = (
  styles: readonly RawStyleBlockServerType[],
  installed: InstalledLibraryTagStylesSettings
): RawStyleBlockServerType[] =>
  _createStyleBlocksForCascadeSorter({
    installedLibrariesTagStylesSettings: installed,
    readTagStyleFields: readRawTagStyleFields,
  })(styles);

/** Paired with the sort, so only this file knows the server block's shape. */
export const isHiddenRawLibraryTagStyle = (
  style: RawStyleBlockServerType,
  installed: InstalledLibraryTagStylesSettings
): boolean => isHiddenLibraryTagStyle(readRawTagStyleFields(style), installed);

/** The raw-block twin of {@link isLibraryTagStyle}, for the publish path. */
export const isRawLibraryTagStyle = (style: RawStyleBlockServerType): boolean =>
  isLibraryTagStyle(readRawTagStyleFields(style));

/**
 * Returns a `styleBlockMap` including all style blocks.
 * If a pluginName is provided, filters by `origin`.
 * If plugins are provided, then style blocks are are decorated with `decorateStyleBlock` utility
 */
export const buildStyleBlockMap = (
  styleBlockState: StyleBlockStateType,
  components: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  pluginNames?: PluginName[]
): StyleBlockMapType => {
  const styleBlockMap: StyleBlockMapType = {};
  const slugMap = Plugins.getPluginsSlugMap(plugins);
  const variantDataMap = generateVariantDataMap(components);
  const orderedStyleBlocks = styleBlockState.getOrderedList({
    slugMap,
    variantDataMap,
    decorate: true,
  });

  const pluginNamesSet = pluginNames?.length ? new Set(pluginNames) : null;

  orderedStyleBlocks.forEach((styleBlock) => {
    if (
      // no filtering
      !pluginNamesSet ||
      // or filter by origin
      (styleBlock.origin &&
        pluginNamesSet.has(getPluginName(styleBlock.origin)))
    ) {
      styleBlockMap[styleBlock._id] = exportStyleBlock(styleBlock, {
        affectsMap: styleBlockState.affectsMap,
        changedRule: styleBlockState.changedRule,
      });
    }
  });
  return styleBlockMap;
};

/**
 * Creates a new default `StyleBlock` for the provided `tagName`, or `null`
 * if `tagName` does not match any items stored in `DEFAULT_TAG_STYLES`.
 */
export const createDefaultTagStyleBlock = (
  tagName: string
): StyleBlock | null => {
  const defaultTag = DEFAULT_TAG_STYLES.find(({tag}) => tag === tagName);

  if (!defaultTag) return null;

  const {
    _id,
    data: {type, name, styleLess},
  } = createDefaultTagServerStyleBlock(defaultTag);
  const rawTagBlock: RawStyleBlockType = {
    _id,
    type,
    name,
    styleLess,
  };

  return buildStyleBlock(rawTagBlock);
};

/**
 * Returns whether the provided style block is a tag-based block included in `styleBlockMap`
 */
const isTagBlockInStyleBlockMap = (
  {_id, type, variants}: StyleBlock,
  styleBlockMap: StyleBlockMapType
) => Boolean(styleBlockMap[_id] && type === 'tag' && variants.size);

/**
 * Function for reifying a "fake" tag-based style block in StyleBlockStore.
 *
 * This function is called on actions that result in copying style data from library tag-based style
 * blocks to site-level style blocks, but where the matching site-level tag block does not yet exist.
 */
const reifyTagStyleBlock = (
  styleBlockState: StyleBlockStateType,
  styleBlockId: string
): StyleBlockStateType => {
  return styleBlockState.withMutations((state) => {
    state.resetChangedRule();
    state.resetAddedRule();
    state.update('styleBlocks', (styleBlocks) =>
      styleBlocks.setIn([styleBlockId, 'fake'], false)
    );
    state.update('styleBlockIds', (styleBlockIds) =>
      styleBlockIds.add(styleBlockId)
    );
  });
};

/**
 * Finds all variant styles matching `variants` within tag-based style blocks
 * and copies that data into site-level tag blocks of the matching tag.
 *
 * If there is not yet a site-level tag block for a source tag block with variants data,
 * a new default style block will be created and returned in updated `styleBlockState`.
 */
const copyVariantsToTagStyleBlocks = ({
  variants,
  styleBlockMap,
  styleBlockState,
}: {
  variants: Component.Variants | undefined;
  styleBlockMap: StyleBlockMapType;
  styleBlockState: StyleBlockStateType;
}) => {
  // When there are no variants provided, there are not tag-based variant styles to copy
  if (!variants) return styleBlockState;

  const variantFieldIds = Object.keys(variants);
  let nextStyleBlockState = styleBlockState;

  // Get a Map of all site tag style blocks that were updated with variant style data
  const updatedTagBlocks = styleBlockState.styleBlocks.reduce(
    (blockAcc, styleBlock) => {
      // Only reduce over tag-based style blocks in `styleBlockMap` that include `styleBlock.variants`
      if (!isTagBlockInStyleBlockMap(styleBlock, styleBlockMap))
        return blockAcc;

      // For each tag block, collect all `styleBlock.variants` where the `key`
      //  contains a `variantFieldId` from `component.variants`.
      const variantsToCopy = styleBlock.variants.reduce(
        (variantAcc, variant, key) =>
          variantFieldIds.some((id) => key.includes(id))
            ? // Although `StyleBlockVariant` suggests only a `styleLess` property exists,
              // it's possible to receive decorated style blocks here where a given style block variant
              // contains a `sel` property with the selector string that's used when generating CSS rules.
              // @ts-expect-error - variant can contain a `sel` property which should be excluded on copy
              variantAcc.set(key, variant.delete('sel'))
            : variantAcc,
        OrderedMap<string, StyleBlockVariant>()
      );

      // If no matches are found, there are no variant styles related to the current component to copy
      if (variantsToCopy.isEmpty()) return blockAcc;

      // The IDs used for local site tag-based style blocks are in the format `default-[tagName]`.
      // Here `styleBlock.name` will be an HTML tag which can be used as part of the tag block ID.
      const siteTagBlockId = createDefaultTagStyleBlockId(styleBlock.name);

      // First look for an existing tag block in `styleBlockState`
      // If found, `variantsToCopy` will need to be merged in with that tag's existing `styleBlock.variants`
      let siteTagBlock = styleBlockState.styleBlocks.get(siteTagBlockId);

      // If an existing tag-based style block wasn't found, it means the site has not yet
      // created a style block for the current tag.
      if (!siteTagBlock) {
        const maybeTagStyleBlock = createDefaultTagStyleBlock(styleBlock.name);
        if (!maybeTagStyleBlock) return blockAcc;

        siteTagBlock = maybeTagStyleBlock;

        // Since a new tag style block has been created, `createStyleBlock` is called to initialize
        // the correct properties for that block on the next `styleBlockState`.
        // Without this step it's possible to lose the new tag block in serialization logic
        // that relies on properties like `styleBlockState.globalOrder`, such as when uninstalling a
        // library on a site that did not yet contain a persisted version of the tag block.
        nextStyleBlockState = createStyleBlock(nextStyleBlockState, {
          styleBlock: siteTagBlock,
        });
      } else if (siteTagBlock.get('fake')) {
        // It's possible to find a default tag block that is stubbed in for the Designer environment where `fake` is `true`.
        // When this happens, we need to set `fake` to `false` and reify the style block in styleBlockState.
        siteTagBlock = siteTagBlock.set('fake', false);
        nextStyleBlockState = reifyTagStyleBlock(
          styleBlockState,
          siteTagBlock._id
        );
      }

      return blockAcc.setIn(
        [siteTagBlockId],
        siteTagBlock.mergeIn(['variants'], variantsToCopy)
      );
    },
    IMap<string, StyleBlock>()
  );

  return updatedTagBlocks.isEmpty()
    ? nextStyleBlockState
    : nextStyleBlockState.mergeIn(['styleBlocks'], updatedTagBlocks);
};

/**
 * Get style blocks needed for a component we're detaching
 * and returns an updated StyleBlockState with copied style blocks
 * with different _id and with no origin, and `idMap` with old ids
 * mapped to the newly created ids.
 */
export const getStylesForDetachedComponent = ({
  componentRender,
  styleBlockMap,
  styleBlockState,
  plugins,
  variants,
  idMap,
}: {
  componentRender: Expression;
  styleBlockMap: StyleBlockMapType;
  styleBlockState: StyleBlockStateType;
  plugins: Plugins.PluginsType;
  variants: Component.Variants | undefined;
  /**
   * Map of original style block ids -> new (copied) style block ids.
   * Existing mappings will be checked for reuse. The input map
   * is NOT mutated - a new map is returned containing existing + new mappings.
   */
  idMap: {[id: string]: string};
}) => {
  const styleBlockIds = getStyleBlockIdsFromComponentRender(
    componentRender,
    styleBlockMap
  );
  const {
    state,
    idMap: updatedIdMap,
    assetIds,
  } = copyStyleBlocks({
    styleBlockIds,
    styleBlockState,
    slugMap: Plugins.getPluginsSlugMap(plugins),
    idMap,
  });
  const nextStyleBlockState = copyVariantsToTagStyleBlocks({
    variants,
    styleBlockMap,
    styleBlockState: state,
  });

  return {
    state: nextStyleBlockState,
    idMap: updatedIdMap,
    assetIds,
  };
};

/**
 * Given a list of style block ids, for each creates a style block copy.
 * Copied style blocks have different _id and no `origin`.
 */
export function copyStyleBlocks({
  styleBlockIds,
  styleBlockState,
  slugMap,
  idMap,
}: {
  styleBlockIds: string[];
  styleBlockState: StyleBlockStateType;
  slugMap: Plugins.PluginsType['slugMap'];
  /**
   * Map of original style block ids -> new (copied) style block ids.
   * Existing mappings will be checked for reuse. The input map
   * is NOT mutated - a new map is returned containing existing + new mappings.
   */
  idMap: {[id: string]: string};
}): {
  /**
   * Map of original style block ids -> new (copied) style block ids.
   * Contains all mappings from input idMap plus any newly created mappings.
   */
  idMap: {[id: string]: string};
  /**
   * Updated styleBlockState
   */
  state: StyleBlockStateType;
  /**
   * List of asset ids found in the copied style blocks
   */
  assetIds: RawFileType['_id'][];
} {
  const assetIds: Set<RawFileType['_id']> = new Set();

  const initialStyleBlockId = generateGuid();
  const styleBlocksToCopy: StyleBlock[] = [];
  styleBlockIds.forEach((styleBlockId: string) => {
    const styleBlock = styleBlockState.styleBlocks.get(styleBlockId);
    if (styleBlock) {
      // Check if we already have a mapping for this style block
      if (!idMap[styleBlockId]) {
        // Only add to copy list if we don't already have it mapped
        styleBlocksToCopy.push(styleBlock);
      }

      // Assuming asset IDs from old style blocks are maintained
      // after copy operation, we collect them here
      getAssetIdsFromStyleBlock(styleBlock.toJS()).forEach((assetId) =>
        assetIds.add(assetId)
      );
    }
  });

  const results = List(styleBlocksToCopy)
    .update(sortByDepth)
    .map((styleBlock: StyleBlock): [string[], StyleBlock] => [[], styleBlock])
    .reduce(accumulateStyleBlocks, {
      importStrategy: 'COPY',
      state: styleBlockState,
      renames: emptyArray,
      droppedFonts: emptyArray,
      nextId: initialStyleBlockId,
      idMap, // Start with the passed idMap instead of empty object
      parentIdMap: emptyObject,
      slugMap,
      // We create the new style blocks without `origin`
      originPlugin: undefined,
    });

  // Let's only update cssPropertyMap if the state has changed
  // otherwise let's return the same state by reference
  const hasStateChanged = results.state !== styleBlockState;

  return {
    idMap: results.idMap,
    state: hasStateChanged
      ? updateCssPropertyMap(results).state
      : results.state,
    assetIds: Array.from(assetIds),
  };
}
